import React, {useEffect, useMemo} from 'react';
import {SubmitHandler, useForm} from 'react-hook-form';
import {useDispatch, useSelector} from 'react-redux';
import {toast} from 'react-toastify';
import {t} from '@lingui/core/macro';

import {statusPageAPI} from 'features/statusPage/api/statusPageAPI';
import {fetchMe, userSelector} from 'entities/user';
import ModalBodyWrapper from 'shared/ui/Modal/ModalBodyWrapper';
import ModalBlockButton from 'shared/ui/Button/ModalBlockButton/ModalBlockButton';
import FormGroupWithError from 'shared/ui/FormGroup/FormGroupWithError';
import InputText from 'shared/ui/Input/InputText';
import NameDescriptionFields from 'shared/ui/Form/NameDescriptionFields/NameDescriptionFields';
import {
    MonitorSelectField,
    MONITORS_MAX,
    validateMonitorsSelection,
    buildMonitorLabels,
} from 'entities/monitor';
import Checkbox from 'shared/ui/Checkbox/Checkbox';
import HideBannerCheckbox from 'shared/ui/HideBannerCheckbox/HideBannerCheckbox';
import CreateStatusPageType from 'features/statusPage/model/StatusPage/CreateStatusPageType';
import {IStatusPageMonitorOutput, StatusPageMonitor} from 'features/statusPage/model/StatusPage/IStatusPage';
import {useCopyToClipboard} from 'shared/lib/hooks/useCopyToClipboard';
import {idsToIri, iriToId} from 'shared/api/iriHelper';
import {TypedDispatch} from 'shared/model/types';

import styles from './StatusPageForm.module.scss';

const SLUG_MIN = 3;
const SLUG_MAX = 100;
const SLUG_PATTERN = /^[a-zA-Z0-9-]+$/;

const extractMonitorUuid = (m: StatusPageMonitor): string => {
    if (typeof m === 'string') {
        return iriToId(m) ?? m;
    }
    return m?.monitor_id ?? '';
};

const orderedMonitorUuids = (monitors: StatusPageMonitor[]): string[] =>
    monitors
        .map((m, index) => ({
            uuid: extractMonitorUuid(m),
            sort: typeof m === 'string' ? index : (m?.sort_order ?? index),
        }))
        .filter((m) => m.uuid)
        .sort((a, b) => a.sort - b.sort)
        .map((m) => m.uuid);

interface IStatusPageFormValues {
    name: string;
    description: string;
    slug: string;
    hide_banner: boolean;
    hide_url: boolean;
    monitors: string[];
}

interface IStatusPageFormProps {
    uuid?: string;
    onSuccess: () => void;
    onClose: () => void;
}

const StatusPageForm: React.FC<IStatusPageFormProps> = ({uuid, onSuccess, onClose}) => {
    const isNew = !uuid;
    const user = useSelector(userSelector);
    const dispatch = useDispatch<TypedDispatch>();
    const {copy} = useCopyToClipboard();

    const {data: fullPage} = statusPageAPI.useGetStatusPageQuery(uuid as string, {skip: isNew});

    const [addStatusPage, {isLoading: isAdding}] = statusPageAPI.useAddStatusPageMutation();
    const [updateStatusPage, {isLoading: isUpdating}] = statusPageAPI.useUpdateStatusPageMutation();
    const [checkSlug] = statusPageAPI.useLazyCheckStatusPageSlugQuery();

    const {
        register,
        handleSubmit,
        control,
        watch,
        setValue,
        setError,
        clearErrors,
        reset,
        formState: {errors, isValid},
    } = useForm<IStatusPageFormValues>({
        mode: 'onChange',
        defaultValues: {name: '', description: '', slug: '', hide_banner: false, hide_url: false, monitors: []},
    });

    register('monitors', {validate: validateMonitorsSelection});

    useEffect(() => {
        if (fullPage) {
            reset({
                name: fullPage.name,
                description: fullPage.description,
                hide_banner: fullPage.hide_banner,
                hide_url: fullPage.hide_url,
                slug: fullPage.slug ?? '',
                monitors: orderedMonitorUuids(fullPage.monitors ?? []),
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fullPage?.uuid]);

    const monitorLabels = useMemo(() => buildMonitorLabels(
        (fullPage?.monitors ?? []).filter((m): m is IStatusPageMonitorOutput => typeof m !== 'string'),
    ), [fullPage?.uuid]); // eslint-disable-line react-hooks/exhaustive-deps

    const selectedMonitors = watch('monitors');
    const slugValue = watch('slug');

    useEffect(() => {
        const slug = (slugValue ?? '').trim();
        if (!slug || !SLUG_PATTERN.test(slug) || slug.length < SLUG_MIN || slug.length > SLUG_MAX) {
            return;
        }
        if (!isNew && slug === fullPage?.slug) {
            clearErrors('slug');
            return;
        }
        const handler = setTimeout(() => {
            checkSlug(slug)
                .unwrap()
                .then((res) => {
                    if (res && !res.available) {
                        setError('slug', {type: 'taken', message: t`Этот URL уже занят или зарезервирован`});
                    } else {
                        clearErrors('slug');
                    }
                })
                .catch(() => undefined);
        }, 500);
        return () => clearTimeout(handler);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [slugValue, isNew, fullPage?.slug]);

    const handleMonitorsChange = (next: string[]) => {
        setValue('monitors', next, {shouldValidate: true, shouldDirty: true});
    };

    const origin = window.location.origin;
    const trimmedSlug = (slugValue ?? '').trim();
    const isSlugChanged = !isNew && trimmedSlug !== (fullPage?.slug ?? '');
    const hasRealUrl = !!trimmedSlug || (!isNew && !!fullPage?.uuid);
    const urlTail = trimmedSlug || (!isNew ? fullPage?.uuid : '{uuid}');
    const previewUrl = `${origin}/status/${urlTail}`;

    const onSubmit: SubmitHandler<IStatusPageFormValues> = async(values) => {
        const payload: CreateStatusPageType = {
            name: values.name.trim(),
            description: values.description.trim(),
            hide_banner: values.hide_banner,
            hide_url: values.hide_url,
            slug: trimmedSlug,
            is_active: isNew ? true : (fullPage?.is_active ?? true),
            monitors: idsToIri(values.monitors, 'monitors'),
        };

        try {
            if (isNew) {
                const created = await addStatusPage(payload).unwrap();
                toast.info(t`Страница создана`);
                dispatch(fetchMe());
                if (created?.url) {
                    copy(created.url);
                }
            } else {
                await updateStatusPage({uuid: uuid as string, ...payload}).unwrap();
                toast.info(t`Изменения сохранены`);
            }
            onSuccess();
        } catch {
        }
    };

    return (
        <form onSubmit={handleSubmit(onSubmit)} className={styles.form}>
            <ModalBodyWrapper className={styles.section}>
                <NameDescriptionFields
                    control={control}
                    register={register}
                    nameError={errors?.name?.message}
                    descriptionError={errors?.description?.message}
                    namePlaceholder={t`Введите название страницы`}
                    descriptionPlaceholder={t`Введите описание страницы`}
                    descriptionRows={5}
                />

                <FormGroupWithError
                    error={errors?.slug?.message}
                    className={styles.formGroup}
                    label={t`Адрес страницы`}
                >
                    <InputText
                        control={control}
                        register={register}
                        name="slug"
                        placeholder={t`Например, my-company`}
                        validate={false}
                        rules={{
                            validate: (v: string) => {
                                const s = (v ?? '').trim();
                                if (!s) {
                                    return true;
                                }
                                if (s.length < SLUG_MIN) {
                                    return t`Минимум ${SLUG_MIN} символа`;
                                }
                                if (s.length > SLUG_MAX) {
                                    return t`Максимум ${SLUG_MAX} символов`;
                                }
                                if (!SLUG_PATTERN.test(s)) {
                                    return t`Только латиница, цифры и дефис — без точек, пробелов и слэшей`;
                                }
                                return true;
                            },
                        }}
                    />
                </FormGroupWithError>
                <span className={styles.counter}>{trimmedSlug.length} / {SLUG_MAX}</span>

                {hasRealUrl ? (
                    <a className={styles.preview} href={previewUrl} target="_blank" rel="noreferrer">
                        {previewUrl}
                    </a>
                ) : (
                    <span className={styles.preview}>{previewUrl}</span>
                )}

                {isSlugChanged && (
                    <span className={styles.warning}>
                        {t`Изменение URL сделает старую ссылку недействительной`}
                    </span>
                )}

                <HideBannerCheckbox
                    register={register}
                    name="hide_banner"
                    enabled={!!user?.tariff.status_page_hide_banner_enabled}
                />

                <div className={styles.hideUrl}>
                    <Checkbox id="hide_url" {...register('hide_url')}>
                        {t`Скрыть URL`}
                    </Checkbox>
                </div>
            </ModalBodyWrapper>

            <MonitorSelectField
                value={selectedMonitors}
                onChange={handleMonitorsChange}
                initialLabels={monitorLabels}
                error={errors?.monitors?.message}
                maxCount={MONITORS_MAX}
            />

            <ModalBlockButton
                primaryButtonText={isNew ? t`Создать` : t`Сохранить`}
                primaryButtonType="submit"
                primaryButtonLoading={isAdding || isUpdating}
                primaryButtonDisabled={!isValid}
                secondaryButtonOnClick={onClose}
            />
        </form>
    );
};

export default StatusPageForm;
